import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  OutlinedInput,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { callApi } from 'utils';
import { OrderIdAutocomplete, Page } from 'components';
import { t } from 'i18n';

const lineStyle = {
  color: 'text.secondary',
  display: 'inline-block',
  textAlign: 'right',
  marginRight: '8px',
  width: '7em',
};
const filterItemSx = {
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
};
const filterControlSx = {
  p: 0,
  flex: 1,
  minWidth: 0,
  width: {xs: '100%', md: 180},
};
const DataLine = ({name, value}) => (
  <Typography sx={{my: 0.5}}>
    <Box component="span" sx={lineStyle}>{name}:</Box>
    {value}
  </Typography>
);

const DataRows = ({rows}) => (
  <Box sx={{width: '100%'}}>
    {rows.map((row) => (<DataLine name={t(row.name)} value={row.value} key={row.name} />))}
  </Box>
);

const sectionColumns = [
  { name: 'Group ID', value: (row) => row.group_id },
  { name: 'Group Name', value: (row) => row.group_name },
  { name: 'FreqCtrl ID', value: (row) => row.fc_id },
  { name: 'Name', value: (row) => row.name },
  { name: 'Total Dids', value: (_row, data) => formatCount(data['Total Dids']) },
  { name: 'Used Dids', value: (_row, data) => formatCount(data['Used Dids']) },
  { name: 'Remaining Dids', value: (_row, data) => formatCount(data['Remaining Dids'], '0') },
  { name: 'Used Dids Rate', value: (_row, data) => formatPercent(Number(data['Used Dids Rate'])) },
  { name: 'Max FreqCtrl', value: (_row, data) => data['Imp FreqCtrl'] },
  { name: 'Average FreqCtrl', value: (_row, data) => formatAverageFreqCtrl(data) },
  { name: 'Estimated Exhaustion Date', value: (row) => formatUsageUntil(row.usage_until) },
  { name: 'Action', value: (row, data, toggleDetail, expanded) => (
    hasFreqCtrlDetail(data) ? (
      <Button size="small" variant="outlined" onClick={() => toggleDetail(row)}>
        {expanded ? '收起' : t('Detail')}
      </Button>
    ) : '-'
  )},
];

const numberFormatter = new Intl.NumberFormat('en-US');

const getSectionData = (section) => Object.fromEntries((section.rows || []).map((row) => [row.name, row.value]));

const formatCount = (value, zeroValue='-') => {
  if (value === undefined || value === null || value === '') return '-';
  const numberValue = Number(value);
  if (numberValue === 0) return zeroValue;
  return Number.isFinite(numberValue) ? numberFormatter.format(numberValue) : value;
};

const parseFreqPercent = (value) => {
  const rows = {};
  const pattern = /(\d+)\s*:\s*([\d.]+)\s*%/g;
  let match;
  while ((match = pattern.exec(value || ''))) {
    const level = Number(match[1]);
    rows[level] = {
      ...(rows[level] || { level }),
      targetPercent: Number(match[2]),
    };
  }
  return rows;
};

const parseFreqStats = (value) => {
  const rows = {};
  const pattern = /(\d+)\s*:\s*([\d.]+)\s*%\s*\/\s*(\d+)/g;
  let match;
  while ((match = pattern.exec(value || ''))) {
    const level = Number(match[1]);
    rows[level] = {
      ...(rows[level] || { level }),
      actualPercent: Number(match[2]),
      usedDids: Number(match[3]),
    };
  }
  return rows;
};

const getFreqCtrlRows = (data) => {
  const percentRows = parseFreqPercent(data['FreqCtrl Percent']);
  const statsRows = parseFreqStats(data['FreqCtrl Stats']);
  return [...new Set([...Object.keys(percentRows), ...Object.keys(statsRows)])]
    .map(Number)
    .sort((a, b) => a - b)
    .map((level) => ({
      level,
      ...percentRows[level],
      ...statsRows[level],
    }));
};

const hasFreqCtrlDetail = (data) => getFreqCtrlRows(data).length > 0;

const calculateAverageFreqCtrl = (data) => {
  const rows = getFreqCtrlRows(data).filter((row) => Number.isFinite(row.usedDids));
  const total = rows.reduce((sum, row) => sum + row.usedDids, 0);
  if (!total) return null;
  const weightedTotal = rows.reduce((sum, row) => sum + row.level * row.usedDids, 0);
  return weightedTotal / total;
};

const formatAverageFreqCtrl = (data) => {
  const configuredAverage = Number(data['Average FreqCtrl']);
  if (Number.isFinite(configuredAverage) && configuredAverage > 0) {
    return configuredAverage.toFixed(2);
  }
  const average = calculateAverageFreqCtrl(data);
  return average === null ? '-' : average.toFixed(2);
};

const formatPercent = (value) => Number.isFinite(value) ? `${value.toFixed(2)}%` : '-';

const formatUsageUntil = (forecast) => {
  if (!forecast) return '-';
  if (forecast.status === 'exhausted') return `${forecast.date}（当日预计用尽）`;
  if (forecast.status === 'covered') return `${forecast.date}（可覆盖至排期结束）`;
  if (forecast.status === 'no-schedule') return '无剩余排期';
  return '暂无法估算';
};

const formatDeviation = (value) => {
  if (!Number.isFinite(value)) return '-';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}pp`;
};

const FreqCtrlDetailTable = ({data}) => (
  <Table size="small" sx={{maxWidth: 720}}>
    <TableHead>
      <TableRow>
        <TableCell>档位</TableCell>
        <TableCell align="right">设置比例</TableCell>
        <TableCell align="right">当前比例</TableCell>
        <TableCell align="right">当前ID数</TableCell>
        <TableCell align="right">偏差</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {getFreqCtrlRows(data).map((row) => (
        <TableRow key={row.level}>
          <TableCell>{row.level}次</TableCell>
          <TableCell align="right">{formatPercent(row.targetPercent)}</TableCell>
          <TableCell align="right">{formatPercent(row.actualPercent)}</TableCell>
          <TableCell align="right">{formatCount(row.usedDids)}</TableCell>
          <TableCell align="right">{formatDeviation(row.actualPercent - row.targetPercent)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const DataTable = ({sections}) => {
  const [expandedSection, setExpandedSection] = useState(null);
  const visibleColumns = sectionColumns.filter(col => col.name !== 'Estimated Exhaustion Date' ||
    sections.some(section => section.usage_until));
  const columns = sections.some((section) => section.error)
    ? [...visibleColumns, { name: 'Remark', value: (row) => row.error && t(row.error) }]
    : visibleColumns;
  const toggleDetail = (section) => setExpandedSection((prev) => prev === section.fc_id ? null : section.fc_id);

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          {columns.map((col) => (
            <TableCell key={col.name}>{t(col.name)}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {sections.map((section) => {
          const data = getSectionData(section);
          const expanded = expandedSection === section.fc_id;
          return (
            <React.Fragment key={section.fc_id}>
              <TableRow>
                {columns.map((col) => {
                  const value = col.value ? col.value(section, data, toggleDetail, expanded) : data[col.name];
                  return (<TableCell key={col.name}>{value === 0 ? '-' : value ?? ''}</TableCell>);
                })}
              </TableRow>
              {expanded && (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{bgcolor: 'action.hover', py: 1.5}}>
                    <FreqCtrlDetailTable data={data} />
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};

const FieldData = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const field = location.pathname.split('/').pop();
  const isGroupField = field === 'devusage';
  const searchKey = isGroupField ? 'groupid' : 'id';
  const spid = searchParams.get(searchKey) || '';
  const [res, setRes] = useState({});
  const [id, setId] = useState(spid);
  const [loading, setLoading] = useState(false);

  useEffect(() => void(setRes({})), [field]);
  useEffect(() => void(setId(spid)), [spid]);

  const handleIdChange = (ev) => {
    setId(ev.target.value);
  };

  const handleQuery = () => {
    setSearchParams(prev => {
      prev.delete(isGroupField ? 'id' : 'groupid');
      prev.set(searchKey, id);
      return prev;
    });
  };

  useEffect(() => {
    if (!spid) return;
    setLoading(true);
    const resource = isGroupField ? 'groups' : 'jobs';
    callApi(`/${resource}/${spid}/fields/${field}`).then((res) => {
      setRes(res);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [spid, field, isGroupField]);

  const { rows, sections, error } = res;

  return (
    <Page title="Device Usage">
      <Paper sx={{p: 2, m: 2}}>
        <Grid container spacing={1} alignItems="center" sx={{my: 1}}>
          <Grid item xs={12} sm={6} md="auto" sx={filterItemSx}>
            <Typography sx={{mr: 0.5, flexShrink: 0}}>{isGroupField ? '订单ID:' : '频控ID:'}</Typography>
            {isGroupField ? (
              <OrderIdAutocomplete
                sx={filterControlSx}
                name={searchKey}
                value={id}
                onChange={handleIdChange}
              />
            ) : (
              <OutlinedInput
                sx={filterControlSx}
                name={searchKey}
                value={id}
                size="small"
                inputProps={{ style: {padding: '6px'} }}
                onChange={handleIdChange}
              />
            )}
          </Grid>
          <Grid item xs={12} sm="auto">
            <Button
              color="primary"
              variant="outlined"
              size="small"
              onClick={handleQuery}
              disabled={loading}
            >
              {loading ? "查询中" : "查询"}
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{my: 2}} />

      {error && (<Alert severity="error">{t(error)}!</Alert>)}

      {sections && (
        <Box sx={{width: '100%'}}>
          <DataTable sections={sections} />
        </Box>
      )}

      {rows && (
        <DataRows rows={rows} />
      )}
      </Paper>
    </Page>
  );
};

export default FieldData;
